import { Injectable } from '@angular/core';
import { APP_CONFIG, Env } from '../../environment';
import { LoggerProvider } from '../logger/logger';

export enum DebugErrorFlow {
  CompleteProfile = 'completeProfile',
  LiteRegistration = 'liteRegistration',
  Login = 'login',
}

export const DEBUG_ERROR_OFF = 'off';
export const DEBUG_ERROR_RANDOM = 'random';

export interface DebugErrorOption {
  value: number | string;
  text: string;
}

const OFF: DebugErrorOption = { value: DEBUG_ERROR_OFF, text: 'Off' };
const RANDOM: DebugErrorOption = { value: DEBUG_ERROR_RANDOM, text: 'Random — new code each submit' };

const COMPLETE_PROFILE_CODES: DebugErrorOption[] = [
  { value: 30, text: '30 - Data quality → Personal Info' },
  { value: 55, text: '55 - Address' },
  { value: 56, text: '56 - City' },
  { value: 58, text: '58 - ID verification' },
  { value: 50, text: '50 - Field validation (ID number)' },
  { value: 70, text: '70 - Duplicate customer' },
  { value: 111, text: '111 - PIN invalid' },
  { value: 140, text: '140 - Password not provided' },
  { value: 150, text: '150 - Password does not match' },
  { value: 160, text: '160 - Password attempts exceeded' },
  { value: 190, text: '190 - Account blocked' },
  { value: 210, text: '210 - Invalid address' },
  { value: 220, text: '220 - Only country resident allowed' },
  { value: 230, text: '230 - Linking knowledge question' },
  { value: 240, text: '240 - Invalid PESEL number' },
  { value: 120, text: '120 - Generic error (fallback)' },
];

const LITE_REGISTRATION_CODES: DebugErrorOption[] = [
  { value: 190, text: '190 - Account blocked' },
  { value: 20, text: '20 - Incorrect mobile number → generic' },
  { value: 50, text: '50 - Verification error → generic' },
  { value: 100, text: '100 - Resource not verified → generic' },
  { value: 105, text: '105 - Verification not found/expired → generic' },
  { value: 110, text: '110 - Password not valid → generic' },
  { value: 120, text: '120 - Error creating user → generic' },
  { value: 130, text: '130 - Error linking → generic' },
  { value: 160, text: '160 - Invalid referral code → generic' },
  { value: 210, text: '210 - Invalid PO box address → generic' },
];

const LOGIN_CODES: DebugErrorOption[] = [
  { value: 10, text: '10 - Invalid grant' },
  { value: 20, text: '20 - Invalid password' },
  { value: 60, text: '60 - OTP errors' },
  { value: 70, text: '70 - No contact data' },
  { value: 80, text: '80 - Verify phone number' },
  { value: 90, text: '90 - Blocked user' },
  { value: 100, text: '100 - Blocked (password)' },
  { value: 110, text: '110 - Blocked (password questions)' },
  { value: 160, text: '160 - No device info' },
  { value: 170, text: '170 - MFA migration not verified' },
  { value: 180, text: '180 - Blocked by OTP failed attempts' },
  { value: 230, text: '230 - Duplicate account' },
  { value: 250, text: '250 - Blocked, cannot send' },
  { value: 270, text: '270 - OTP channel picker' },
  { value: 310, text: '310 - Pending signin invalid' },
];

const byValueAsc = (codes: DebugErrorOption[]): DebugErrorOption[] =>
  [...codes].sort((a, b) => (a.value as number) - (b.value as number));

const CODES_BY_FLOW: { [flow: string]: DebugErrorOption[] } = {
  [DebugErrorFlow.CompleteProfile]: [OFF, RANDOM, ...byValueAsc(COMPLETE_PROFILE_CODES)],
  [DebugErrorFlow.LiteRegistration]: [OFF, RANDOM, ...byValueAsc(LITE_REGISTRATION_CODES)],
  [DebugErrorFlow.Login]: [OFF, RANDOM, ...byValueAsc(LOGIN_CODES)],
};

@Injectable()
export class DebugErrorProvider {
  private forcedByFlow: { [flow: string]: number | string } = {};

  getCodesForFlow(flow: DebugErrorFlow): DebugErrorOption[] {
    return CODES_BY_FLOW[flow] || [OFF];
  }

  setForcedError(flow: DebugErrorFlow, value: number | string) {
    if (value === null || value === undefined || value === '' || value === DEBUG_ERROR_OFF) {
      delete this.forcedByFlow[flow];
    } else {
      this.forcedByFlow[flow] = value;
    }
  }

  getRawSetting(flow: DebugErrorFlow): number | string {
    const value = this.forcedByFlow[flow];
    return value === undefined ? DEBUG_ERROR_OFF : value;
  }

  getForcedError(flow: DebugErrorFlow): number {
    if (APP_CONFIG.env === Env.PROD || !APP_CONFIG.showDebug) {
      return null;
    }
    const value = this.forcedByFlow[flow];
    if (value === null || value === undefined || value === '' || value === DEBUG_ERROR_OFF) {
      return null;
    }
    if (value === DEBUG_ERROR_RANDOM) {
      return this.pickRandomCode(flow);
    }
    const code = Number(value);
    return Number.isFinite(code) ? code : null;
  }

  private pickRandomCode(flow: DebugErrorFlow): number {
    const codes = this.getCodesForFlow(flow).map((entry) => entry.value).filter((value) => typeof value === 'number') as number[];
    if (codes.length === 0) {
      return null;
    }
    const code = codes[Math.floor(Math.random() * codes.length)];
    LoggerProvider.info(this, `DebugErrorProvider: random forced code for ${flow} = ${code}`);
    return code;
  }
}



// WEBPACK FOOTER //
// ./src/providers/debug-error/debug-error.ts